import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { X, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';

const TIER_COLORS = {
  bronze: 'from-amber-600 to-amber-800',
  silver: 'from-gray-300 to-gray-500',
  gold: 'from-yellow-400 to-amber-500',
  diamond: 'from-cyan-400 to-blue-600'
};

const TIER_LABELS = {
  bronze: '🥉 Bronze',
  silver: '🥈 Silver',
  gold: '🥇 Gold',
  diamond: '💎 Diamond'
};

const BadgeNotification = ({ badge, onClose }) => {
  useEffect(() => {
    // Fire confetti when badge is earned
    if (badge) {
      const duration = 3000;
      const end = Date.now() + duration;
      
      const colors = badge.tier === 'diamond' ? ['#00ffff', '#0080ff', '#ffffff'] :
                     badge.tier === 'gold' ? ['#ffd700', '#ffb300', '#ffffff'] :
                     badge.tier === 'silver' ? ['#c0c0c0', '#808080', '#ffffff'] :
                     ['#cd7f32', '#b8860b', '#ffffff'];

      (function frame() {
        confetti({
          particleCount: 3,
          angle: 60,
          spread: 55,
          origin: { x: 0, y: 0.7 },
          colors: colors
        });
        confetti({
          particleCount: 3,
          angle: 120,
          spread: 55,
          origin: { x: 1, y: 0.7 },
          colors: colors
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      }());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [badge]);

  if (!badge) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.5, opacity: 0, y: 50 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.5, opacity: 0, y: 50 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="bg-white rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Badge Icon */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", delay: 0.2, duration: 0.8 }}
            className={`w-32 h-32 mx-auto mb-6 rounded-full bg-gradient-to-br ${TIER_COLORS[badge.tier]} flex items-center justify-center shadow-lg`}
          >
            <span className="text-6xl">{badge.icon}</span>
          </motion.div>

          {/* Celebration Text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <p className="text-sm font-semibold text-primary mb-2">🎉 NEW BADGE UNLOCKED!</p>
            <h2 className="font-heading text-3xl font-bold mb-2">{badge.name}</h2>
            <p className="text-muted-foreground mb-4">{badge.description}</p>
            
            {/* Tier Badge */}
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${TIER_COLORS[badge.tier]} text-white font-semibold mb-6`}>
              {TIER_LABELS[badge.tier]}
            </div>
          </motion.div>

          <Button
            onClick={onClose}
            className="w-full rounded-full h-12 font-bold"
          >
            Awesome! 🙌
          </Button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BadgeNotification;
